import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useFilePreview } from "@jugl-web/domain-resources/files/providers/FilePreviewProvider";
import {
  ChatMessageAttachmentType,
  ChatMessagePayloadAttachment,
} from "@jugl-web/rest-api";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { cx, useTranslations } from "@jugl-web/utils";
import { InteractiveContainer } from "@jugl-web/ui-kit";
import { getAttachmentsCountLabel } from "@web-src/modules/chats/utils/getAttachmentsCountLabel";
import animationData from "./assets/loading-animation.json";
import { ReactComponent as ImagePlaceholder } from "./assets/image-placeholder.svg";
import { ReactComponent as PlayIcon } from "./assets/play-icon.svg";
import { useChatMessageBubble } from "../../providers/ChatMessageBubbleProvider/useChatMessageBubble";
import { ChatMessageBubbleAction } from "../../types/ChatMessageBubbleAction";

const MAX_VISIBLE = 4;
const MAX_SIZE = 300;

const getDimensions = (
  width?: number,
  height?: number
): { width: number; height: number } | null => {
  if (!width || !height) {
    return null;
  }
  let reduceBy = 1;
  if (width >= height && width > MAX_SIZE) {
    reduceBy = MAX_SIZE / width;
  } else if (width <= height && height > MAX_SIZE) {
    reduceBy = MAX_SIZE / height;
  }
  return {
    width: Math.round(width * reduceBy),
    height: Math.round(height * reduceBy),
  };
};

export const MediaPreviewAttachment: FC = () => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const {
    attachments = [],
    hasContentProtectionRestriction,
    isPending,
    availableActions,
    getAttachmentUrls,
    onDeleteAttachment,
    isAttachmentOpeningDisabled,
  } = useChatMessageBubble();

  const { previewFiles, previewFile, closePreview, currentIndex } =
    useFilePreview();
  const { t } = useTranslations();
  const [previewStates, setPreviewStates] = useState<
    Record<number, "loading" | "error" | "loaded">
  >({});

  const visibleAttachments = attachments.slice(0, MAX_VISIBLE);
  const extraCount = attachments.length - MAX_VISIBLE;
  const isMultipleAttachments = attachments.length > 1;

  const createPreviewObject = useCallback(
    (attachment: ChatMessagePayloadAttachment, isMultiple?: boolean) => {
      const { stream, preview } = getAttachmentUrls(attachment);

      return {
        url: stream,
        caption: attachment.caption,
        preview,
        name:
          attachment.name ||
          t({
            id: "chats-page.chat-file",
            defaultMessage: "Chat File",
          }),
        mimeType: attachment.mimetype,
        onDownload: !hasContentProtectionRestriction
          ? () => window.open(stream, "_blank")
          : undefined,
        onDelete:
          isMultiple &&
          onDeleteAttachment &&
          availableActions?.includes(ChatMessageBubbleAction.EDIT)
            ? () => onDeleteAttachment(attachment.uid)
            : undefined,
        onPreviewClose: () => setIsPreviewOpen(false),
      };
    },
    [
      availableActions,
      getAttachmentUrls,
      hasContentProtectionRestriction,
      onDeleteAttachment,
      t,
    ]
  );

  const handlePreview = useCallback(
    (index: number) => {
      const attachment = attachments[index];
      const previewData = createPreviewObject(attachment);

      setIsPreviewOpen(true);

      if (attachments.length === 1) {
        previewFile(previewData);
      } else {
        const previewFilesData = attachments.map((file) =>
          createPreviewObject(file, true)
        );
        previewFiles(previewFilesData, previewData.url);
      }
    },
    [attachments, createPreviewObject, previewFile, previewFiles]
  );

  useEffect(() => {
    if (isPreviewOpen) {
      if (!!attachments.length && typeof currentIndex === "number") {
        if (attachments[currentIndex]) {
          handlePreview(currentIndex);
          return;
        }
        handlePreview(attachments.length - 1);
      } else {
        closePreview();
      }
    }
  }, [isPreviewOpen, currentIndex, handlePreview, attachments, closePreview]);

  const { totalSize, sendSize } = useMemo(() => {
    if (!isPending) {
      return { totalSize: 0, sendSize: 0 };
    }

    const computedTotalSize = attachments.reduce(
      (sum, attachment) => sum + attachment.size,
      0
    );

    const computedSendSize = attachments.reduce(
      (sum, attachment) =>
        sum + (attachment.size * (attachment._progress || 0)) / 100,
      0
    );

    return { totalSize: computedTotalSize, sendSize: computedSendSize };
  }, [attachments, isPending]);

  return (
    <div
      className={cx(
        "relative flex w-full justify-center overflow-hidden rounded",
        visibleAttachments.length > 1 && "grid auto-rows-fr grid-cols-2 gap-1",
        !isMultipleAttachments &&
          "rounded bg-gradient-to-b from-[#F4F6F7] to-[#CDE2F2]"
      )}
    >
      {isPending && (
        <div className="absolute top-1/2 left-1/2 z-10 flex w-[110px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-3xl bg-messages-chat-bubble-sender py-2 text-white">
          <span className="font-secondary text-xs leading-[130%] text-white">
            {t(
              {
                id: "chats-page.uploading-percentage",
                defaultMessage: "Uploading {percentage}%",
              },
              { percentage: ((sendSize / totalSize) * 100).toFixed(0) }
            )}
          </span>
        </div>
      )}
      {visibleAttachments.map((attachment, index) => {
        const { preview, stream } = getAttachmentUrls(attachment);
        const isVideo = attachment.type === ChatMessageAttachmentType.video;
        const isGif = attachment.type === ChatMessageAttachmentType.gif;
        const previewState = previewStates[index] || "loading";

        const isSingle = visibleAttachments.length === 1;
        const isMoreThanTwo = visibleAttachments.length > 2;
        const isTripleFirst = visibleAttachments.length === 3 && index === 0;

        const dimensions = isSingle
          ? getDimensions(attachment.width, attachment.height)
          : undefined;

        const getStyle = (): React.CSSProperties => {
          if (isSingle) {
            return {
              width: `${dimensions?.width || MAX_SIZE}px`,
              height: `${dimensions?.height || MAX_SIZE}px`,
            };
          }

          if (isMoreThanTwo) {
            return {
              width: "210px",
              height: isTripleFirst ? "100%" : "160px",
            };
          }

          return {
            width: "230px",
            height: "180px",
          };
        };

        return (
          <InteractiveContainer
            key={+index}
            onClick={() => handlePreview(index)}
            isDisabled={
              isAttachmentOpeningDisabled ||
              attachment.type === ChatMessageAttachmentType.gif
            }
            className={cx(
              "relative flex min-h-[30px] w-full items-center justify-center overflow-hidden rounded bg-gradient-to-b from-[#F4F6F7] to-[#CDE2F2]",
              isTripleFirst && "row-span-2",
              visibleAttachments.length === 1 &&
                "bg-gradient-to-b from-[#F4F6F7] to-[#CDE2F2]"
            )}
            style={getStyle()}
          >
            {previewState === "loading" && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Lottie options={{ animationData }} height={32} width={32} />
              </div>
            )}

            {previewState === "error" && (
              <div className="absolute inset-0 flex items-center justify-center">
                {isVideo ? <PlayIcon /> : <ImagePlaceholder />}
              </div>
            )}

            {previewState === "loaded" && isVideo && (
              <div className="absolute inset-0 flex items-center justify-center">
                <PlayIcon />
              </div>
            )}

            <img
              onLoad={() =>
                setPreviewStates((prev) => ({ ...prev, [index]: "loaded" }))
              }
              onError={() =>
                setPreviewStates((prev) => ({ ...prev, [index]: "error" }))
              }
              className={cx("h-full w-full object-cover", {
                invisible: previewState !== "loaded",
              })}
              src={isGif ? stream : preview}
              alt={attachment.name}
            />

            {index === MAX_VISIBLE - 1 && extraCount > 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 font-secondary text-sm text-white">
                +{getAttachmentsCountLabel(attachment.type, extraCount, t)}
              </div>
            )}
          </InteractiveContainer>
        );
      })}
    </div>
  );
};
